<script setup lang="ts">
import type {ContentGlossary} from '@/ts/types/dto/home-dto';
import {computed} from 'vue';
import type {SideBarGroupedSection} from '@/ts/types/component/balm-sidebar';
import SidebarName from '@/vue/molecules/sidebar/sidebar-name.vue';
import {groupByCategory} from '@/ts/composables/pure/use-content-sidebar';

const props = defineProps<{
  glossaries: ContentGlossary[];
}>();

function trimTrailingEmptyParagraphs(html: string): string {
  return html?.replace(/(?:\s*<p[^>]*>\s*(?:&nbsp;|\s)*<\/p>\s*)+$/iu, '') ?? '';
}

const grouped = computed<SideBarGroupedSection[]>(() => groupByCategory(props.glossaries, item => ({
  id: item.id,
  html: trimTrailingEmptyParagraphs(item.definition),
  label: item.term,
})));

</script>
<template>
  <sidebar-name :name="$t('glossary')"/>
  <div class="glossary">
    <template v-for="group in grouped" :key="group.id">
      <div class="glossary-category">
        {{ group.name }}
      </div>
      <div v-for="term in group.items" :key="term.id" class="glossary-item">
        <div class="glossary-term">
          {{ term.label }}
        </div>
        <div v-safe-html="term.html" class="glossary-definition"/>
      </div>
    </template>
  </div>
</template>
<style scoped lang="scss">
.glossary {
  margin: var(--Spacing-spacing-4) 0;
}

.glossary-category {
  @include font-body-bold;
  margin-top: var(--Spacing-spacing-8);
  margin-bottom: var(--Spacing-spacing-3);
  color: var(--Neutral-UI-neutral-ui-60);

  &:first-child {
    margin-top: var(--Spacing-spacing-4);
  }
}

.glossary-item {
  padding-top: var(--Spacing-spacing-4);
  padding-bottom: var(--Spacing-spacing-5);

  @include screen-lg {
    display: grid;
    grid-template-columns: 1fr 1fr
  }
}

.glossary-item + .glossary-item {
  border-top: 1px solid var(--Neutral-UI-neutral-ui-20, #DCD5D0);
}

.glossary-term {
  @include font-body-bold;
  padding-right: var(--Spacing-spacing-8);

  @include screen-lg {
    grid-column: 1;
  }
}

.glossary-definition {
  @include font-body-sm;
  padding-right: var(--Spacing-spacing-7);

  @include screen-lg {
    grid-column: 2;
  }

  :deep(p:last-child) {
    margin-bottom: 0;
  }
}
</style>
